import { useState } from "react";
import { CheckCircle2, Printer, X } from "lucide-react";
import { Dialog, DialogContent } from "../utils/components/ui/dialog";
import InvoiceDownloadDialog from "./InvoiceDownloadDialog";

interface QuoteSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPrintQuote: () => void;
  quoteId: string;
  title?: string;
  subtitle?: string;
}

export default function QuoteSuccessModal({
  isOpen,
  onClose,
  quoteId,
  title = "Draft Created Successfully",
  subtitle = "Draft ID",
}: QuoteSuccessModalProps) {
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[420px] p-0 gap-0 rounded-[24px] border-none shadow-[0_24px_60px_rgba(26,94,249,0.18)] overflow-hidden bg-white">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 rounded-full p-1.5 text-[#8A9BB5] hover:bg-[#F0F4FF] hover:text-[#1A5EF9] transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Blue gradient header */}
          <div className="relative bg-gradient-to-br from-[#1A5EF9] to-[#3B82F6] px-8 pt-10 pb-14 flex flex-col items-center">
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2" />

            {/* Check icon */}
            <div className="relative z-10 rounded-full bg-white/20 p-3 mb-3">
              <div className="rounded-full bg-white p-3">
                <CheckCircle2
                  className="h-8 w-8 text-[#1A5EF9]"
                  strokeWidth={2}
                />
              </div>
            </div>

            <h2 className="relative z-10 text-xl font-semibold text-white text-center mt-1">
              {title}
            </h2>
          </div>

          {/* Content */}
          <div className="px-8 pt-6 pb-8 -mt-6 relative">
            {/* ID badge */}
            {quoteId && (
              <div className="flex items-center justify-center mb-6 mt-4">
                <div className="rounded-[12px] border border-[#E8EEF9] bg-[#F5F8FD] px-5 py-3 text-center">
                  <p className="text-xs font-medium text-[#8A9BB5] mb-0.5 uppercase tracking-wide">
                    {subtitle}
                  </p>
                  <p className="text-base font-bold text-[#1A5EF9]">
                    {quoteId}
                  </p>
                </div>
              </div>
            )}

            <p className="text-center text-sm text-[#7D8998] mb-6">
              Your record has been saved. You can print it now or close to
              continue.
            </p>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => setDownloadDialogOpen(true)}
                className="h-11 w-full rounded-[12px] bg-[#1A5EF9] text-white hover:bg-[#0E4FDE] font-medium text-sm flex items-center justify-center gap-2 transition-colors"
              >
                <Printer className="h-4 w-4" />
                Print
              </button>
              <button
                onClick={onClose}
                className="h-11 w-full rounded-[12px] border border-[#D7E2F5] text-[#52627C] hover:bg-[#F5F8FD] font-medium text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <InvoiceDownloadDialog
        open={downloadDialogOpen}
        invoiceId={quoteId}
        onClose={() => setDownloadDialogOpen(false)}
      />
    </>
  );
}
